import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiRefreshCw, FiEdit3 } from 'react-icons/fi';
import { getCarrierSettings, setAnswerRules } from '../../services/automationService';

interface CarrierAnswerRulesProps {
  carrierId: string;
  carrierName: string;
}

/**
 * The agency's default answers for one carrier, like the rater's rating rules: one "question: answer" per line. When a quote for this
 * carrier would stop to ask on a card, a matching rule answers it instead (automation/lib/answerRules.ts). Consent, disclosures,
 * credit, sign-in codes and who lives at the address are always asked.
 */
const CarrierAnswerRules: React.FC<CarrierAnswerRulesProps> = ({ carrierId, carrierName }) => {
  const [text, setText] = useState('');
  const [savedText, setSavedText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    getCarrierSettings()
      .then((s) => {
        if (!mounted.current) return;
        const current = s.answerRules?.[carrierId] ?? '';
        setText(current);
        setSavedText(current);
      })
      .catch(() => {})
      .finally(() => { if (mounted.current) setLoading(false); });
    return () => { mounted.current = false; };
  }, [carrierId]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const s = await setAnswerRules(carrierId, text);
      if (!mounted.current) return;
      setSavedText(s.answerRules?.[carrierId] ?? '');
      setSaved(true);
      setTimeout(() => { if (mounted.current) setSaved(false); }, 3000);
    } catch (err) {
      if (mounted.current) setError(err instanceof Error ? err.message : 'Could not save the rules.');
    }
    if (mounted.current) setSaving(false);
  };

  return (
    <div className="ca-session ca-rules">
      <label className="ca-session__label" htmlFor={`ca-rules-${carrierId}`}>
        <FiEdit3 size={13} /> Default answers for {carrierName}
      </label>
      <div className="ca-session__hint">
        When a {carrierName} quote asks a question you always answer the same way, write the answer here, one per line as
        &ldquo;question: answer&rdquo;. A matching question is answered for you and listed in the quote&apos;s notes. Write
        &ldquo;skip&rdquo; to skip a question {carrierName} doesn&apos;t need for a quote. Consent, disclosures, credit, sign-in codes
        and who lives at the address are always asked.
      </div>
      <textarea
        id={`ca-rules-${carrierId}`}
        className="ca-run__input ca-rules__textarea"
        rows={6}
        disabled={loading || saving}
        value={text}
        maxLength={8000}
        placeholder={'Employment status: Retired\nLicense number: skip\nYears at current address: 5'}
        onChange={(e) => setText(e.target.value)}
      />
      {error && <div className="ca-rules__error">{error}</div>}
      <div className="ca-rules__footer">
        <button type="button" className="ca-run__btn ca-rules__save" onClick={handleSave} disabled={loading || saving || text === savedText}>
          {saving ? <FiRefreshCw size={13} className="spin" /> : <FiCheck size={13} />}
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save rules'}
        </button>
      </div>
    </div>
  );
};

export default CarrierAnswerRules;
